import React from 'react';
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  CircleDollarSign,
  MapPin,
  MessageSquare,
  Package,
  Store,
  Truck,
} from 'lucide-react';

const STATUS_STYLES = {
  entregado: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rechazado: 'bg-red-100 text-red-800 border-red-200',
  cancelado: 'bg-slate-200 text-slate-800 border-slate-300',
  postergado: 'bg-amber-100 text-amber-800 border-amber-200',
  encamino: 'bg-blue-100 text-blue-800 border-blue-200',
  en_recojo: 'bg-violet-100 text-violet-800 border-violet-200',
  pending: 'bg-sky-100 text-sky-800 border-sky-200',
};

function formatDate(value, withTime = false) {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
}

function channelLabel(message) {
  if (message.channel === 'whatsapp') {
    return message.direction === 'inbound' ? 'WHATSAPP · CLIENTE' : 'WHATSAPP · FAST MAX';
  }
  if (message.sender_type === 'system' || message.channel === 'system') return 'SISTEMA';
  return `INTERNO · ${(message.sender_type || 'OPERACIONES').toUpperCase()}`;
}

export default function DeliveryIntelligenceCard({ delivery, onAction }) {
  const history = delivery.status_history || [];
  const messages = delivery.communication_messages || [];
  const alerts = delivery.alerts || [];

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Entrega #{delivery.id}</p>
          <h3 className="mt-1 text-lg font-extrabold text-slate-900">{formatDate(delivery.delivery_date)}</h3>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-extrabold uppercase ${STATUS_STYLES[delivery.status] || 'border-slate-200 bg-slate-100 text-slate-700'}`}>
          {delivery.status || 'sin estado'}
        </span>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Info icon={Store} label="Tienda" value={delivery.seller_username || 'Sin tienda asignada'} />
          <Info icon={MapPin} label="Distrito" value={delivery.district || 'Sin distrito'} />
          <Info icon={Package} label="Producto" value={delivery.product || 'Sin detalle'} />
          <Info icon={CircleDollarSign} label="Cobro" value={delivery.charge == null ? 'Sin monto' : `S/ ${Number(delivery.charge).toFixed(2)}`} />
          <Info icon={Truck} label="Driver" value={delivery.driver_username || 'Sin asignar'} />
          <Info icon={MessageSquare} label="Notas" value={delivery.notes_driver || delivery.notes || 'Sin notas'} />
        </div>

        <section>
          <h4 className="mb-3 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">Historial de estado</h4>
          {history.length ? (
            <div className="space-y-2">
              {history.map((entry, index) => (
                <div key={entry.id || `${entry.to_status}-${index}`} className="flex gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-brand-primary ring-4 ring-brand-primary/10" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 text-sm font-bold text-slate-800">
                      <span>{entry.from_status || 'inicio'}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                      <span>{entry.to_status}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{formatDate(entry.created_at, true)}</p>
                    {entry.reason && <p className="mt-1 text-sm text-slate-700"><strong>Motivo:</strong> {entry.reason}</p>}
                    {entry.note && <p className="text-sm text-slate-600"><strong>Nota:</strong> {entry.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-500">No hay cambios de estado registrados.</p>
          )}
        </section>

        {alerts.length > 0 && (
          <section>
            <h4 className="mb-2 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">Alertas operativas</h4>
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div key={alert.id} className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-bold">{alert.alert_type} · {alert.severity}</p>
                    <p>{alert.message || 'Sin detalle adicional'}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {messages.length > 0 && (
          <section>
            <h4 className="mb-2 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">Comunicación reciente</h4>
            <div className="space-y-2">
              {messages.slice(0, 5).map((message) => (
                <div key={`${message.source}-${message.id}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    <span>{channelLabel(message)}</span>
                    <span>{formatDate(message.created_at, true)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-800">{message.content || `[${message.message_type}]`}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {delivery.is_active && (
          <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row">
            <button
              type="button"
              onClick={() => onAction(delivery, 'postpone')}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-brand-primary px-4 py-2.5 text-sm font-extrabold text-brand-primary hover:bg-brand-primary/5"
            >
              <CalendarClock className="h-4 w-4" />
              Postergar
            </button>
            <button
              type="button"
              onClick={() => onAction(delivery, 'cancel')}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-red-700"
            >
              <AlertTriangle className="h-4 w-4" />
              Cancelar entrega
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex min-w-0 gap-3 rounded-xl border border-slate-100 bg-white p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" />
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="break-words font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
